import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const fmtDay = (isoDate) => {
  if (!isoDate) return null;
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-NZ', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

// PostgREST sends .in() as a query string, so chunk the id list rather than
// putting several hundred order ids into one URL as the history grows.
const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Orders that have at least one blocked_order_notes entry (so they were once
// on Blocked Orders) but whose current stage is no longer Problem Encountered
// or To Call Customer -- i.e. they've moved on and aren't being worked today.
// The RPC only carries each order's latest note; the full note history is
// loaded alongside it so a row can be expanded without a further round trip.
const BlockedOrdersHistory = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [notesByOrder, setNotesByOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showDispatched, setShowDispatched] = useState(false);
  const [search, setSearch]   = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_blocked_orders_history');
    if (error) {
      console.error('get_blocked_orders_history error:', error);
      setRows([]);
      setNotesByOrder({});
      setLoading(false);
      setLastRefresh(new Date());
      return;
    }

    const historyRows = data || [];
    setRows(historyRows);

    const map = {};
    const ids = historyRows.map((r) => r.order_id);
    for (const batch of chunk(ids, 200)) {
      const { data: notes, error: notesError } = await supabase
        .from('blocked_order_notes')
        .select('id, order_id, note, initials, created_at, stage_at_note, follow_up_date')
        .in('order_id', batch)
        .order('created_at', { ascending: true });
      if (notesError) {
        console.error('blocked_order_notes error:', notesError);
        continue;
      }
      for (const n of notes || []) {
        (map[n.order_id] ??= []).push(n);
      }
    }
    setNotesByOrder(map);

    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpanded = (orderId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  };

  const term = search.trim().toLowerCase();
  const filtered = rows
    .filter((r) => showDispatched || r.stage !== 'Dispatched')
    .filter((r) => !term || (r.order_ref || '').toLowerCase().includes(term));

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/')}>← Home</button>
        <span className="page-nav__title">Blocked Orders History</span>
        <button className="back-btn" onClick={() => navigate('/blocked-orders')}>🚧 Blocked Orders</button>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">
          <SectionHeader
            title="Previously Blocked"
            count={filtered.length}
            onRefresh={load}
            loading={loading}
            actions={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Search order… e.g. OJUN84407-33"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    background: 'var(--bg3)', color: 'var(--white)',
                    border: '1px solid var(--border2)', borderRadius: 4,
                    padding: '4px 10px', fontFamily: 'var(--font-body)',
                    fontSize: 12, width: 230,
                  }}
                />
                {search && (
                  <button
                    className="action-btn action-btn--ghost"
                    onClick={() => setSearch('')}
                    style={{ fontSize: 12 }}
                  >
                    Clear
                  </button>
                )}
                <button
                  className={`action-btn ${showDispatched ? 'action-btn--active' : 'action-btn--ghost'}`}
                  onClick={() => setShowDispatched((v) => !v)}
                  style={{ fontSize: 12 }}
                >
                  {showDispatched ? 'Showing Dispatched ✓' : 'Show Dispatched'}
                </button>
              </div>
            }
          />
          <p className="section__desc" style={{ padding: '0 16px' }}>
            Orders that have a note logged against them (so appeared on Blocked Orders at some
            point) but are no longer in Problem Encountered or To Call Customer — newest first.
            Click a row to see its full note history.
            {!showDispatched && ' Dispatched orders are hidden by default.'}
          </p>

          {loading ? <div className="dash-loading">Loading…</div> : (
            <table className="dash-table">
              <thead>
                <tr>
                  <th>History</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Stage</th>
                  <th>Order Date</th>
                  <th>Last Note</th>
                  <th>Last Note Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const notes  = notesByOrder[row.order_id] || [];
                  const isOpen = expanded.has(row.order_id);
                  return (
                    <React.Fragment key={row.order_id}>
                      <tr onClick={() => toggleExpanded(row.order_id)} style={{ cursor: 'pointer' }}>
                        <td style={{ whiteSpace: 'nowrap', color: 'var(--gold)' }}>
                          {isOpen ? '▾ Hide' : '▸ Show'} ({notes.length})
                        </td>
                        <td><span className="code-badge">{row.order_ref}</span></td>
                        <td>{row.status || '—'}</td>
                        <td>{row.stage || '—'}</td>
                        <td className="muted">{fmtDate(row.created_date)}</td>
                        <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                          {row.last_note_initials && (
                            <span style={{ color: 'var(--gold)', marginRight: 6 }}>[{row.last_note_initials}]</span>
                          )}
                          {row.last_note || '—'}
                        </td>
                        <td className="muted">{fmtDate(row.last_note_at)}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={7} style={{ background: 'var(--bg3)', padding: '10px 16px' }}>
                            {!notes.length ? (
                              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                                No notes found for this order.
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {notes.map((n) => (
                                  <div key={n.id} style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'normal' }}>
                                    <span style={{ color: 'var(--white)' }}>{fmtDate(n.created_at)}</span>
                                    {n.initials && (
                                      <span style={{ color: 'var(--gold)', marginLeft: 6 }}>[{n.initials}]</span>
                                    )}
                                    {n.stage_at_note && (
                                      <span style={{ marginLeft: 6 }}>({n.stage_at_note})</span>
                                    )}
                                    <span style={{ marginLeft: 6, color: 'var(--white)' }}>{n.note}</span>
                                    {n.follow_up_date && (
                                      <span style={{ marginLeft: 6 }}>— follow-up {fmtDay(n.follow_up_date)}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!filtered.length && (
                  <tr>
                    <td colSpan={7} className="dash-empty">
                      {term ? `No orders matching "${search.trim()}"` : 'No resolved blocked orders'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
};

export default BlockedOrdersHistory;
